// The band: one synthesised instrument per voice of the soundtrack (songs.ts), drawn with the synth kit into the
// player's layer buses. Each takes a start time (s), a length (s), a pitch (Hz) and a velocity (0-1).
import { tone, fm, noise, thump, click, supersaw, pluck, type Kit } from '../synth.js';
import type { Inst } from './songs.js';

export type Instrument = (k: Kit, at: number, dur: number, f: number, vel: number) => void;

/** Higher notes sit a little to the right, as on a keyboard seen from the audience. */
const keyPan = (f: number) => Math.max(-0.5, Math.min(0.5, Math.log2(f / 440) * 0.3));

export const INSTRUMENTS: Record<Inst, Instrument> = {
  // ---- drums ----
  kick: (k, at, _d, _f, v) => {
    thump(k, at, 150, 44, 0.3, 0.9 * v);
    tone(k, { at, dur: 0.1, vol: 0.3 * v, f: 190, to: 60, type: 'triangle', lp: 900, attack: 0.001 });
    click(k, at, 3200, 0.22 * v);
  },
  snare: (k, at, _d, _f, v) => {
    noise(k, { at, dur: 0.18, vol: 0.45 * v, filter: 'bandpass', f: 1900, q: 0.8, attack: 0.001 });
    noise(k, { at, dur: 0.08, vol: 0.18 * v, filter: 'highpass', f: 6000, attack: 0.0008 });
    tone(k, { at, dur: 0.09, vol: 0.25 * v, f: 190, to: 150, type: 'triangle', attack: 0.001 });
  },
  clap: (k, at, _d, _f, v) => {
    for (const [d, a] of [[0, 1], [0.009, 0.8], [0.019, 0.9]]) noise(k, { at: at + d, dur: 0.012, vol: 0.5 * v * a, filter: 'bandpass', f: 1300, q: 1.4, attack: 0.0006 });
    noise(k, { at: at + 0.026, dur: 0.13, vol: 0.28 * v, filter: 'bandpass', f: 1100, q: 0.9, attack: 0.001 });
  },
  hatC: (k, at, _d, _f, v) => noise(k, { at, dur: 0.045, vol: 0.17 * v, filter: 'highpass', f: 8000, q: 0.7, attack: 0.001, pan: 0.2 }),
  hatO: (k, at, d, _f, v) => noise(k, { at, dur: Math.min(0.28, Math.max(0.12, d)), vol: 0.13 * v, filter: 'highpass', f: 7000, q: 0.7, attack: 0.001, pan: 0.2 }),
  shaker: (k, at, _d, _f, v) => noise(k, { at, dur: 0.09, vol: 0.18 * v, filter: 'bandpass', f: 6500, q: 1.2, attack: 0.015, pan: -0.25 }),
  rim: (k, at, _d, _f, v) => { tone(k, { at, dur: 0.035, vol: 0.25 * v, f: 820, to: 560, type: 'sine', attack: 0.0008, pan: 0.3 }); click(k, at, 2500, 0.15 * v, 0.3); },
  crash: (k, at, _d, _f, v) => {
    noise(k, { at, dur: 1.8, vol: 0.22 * v, filter: 'highpass', f: 5000, q: 0.6, attack: 0.004 });
    noise(k, { at, dur: 1.2, vol: 0.1 * v, filter: 'bandpass', f: 9000, q: 1, attack: 0.002, pan: 0.3 });
  },
  // a riser peaks just before its end: the drop lands on the first beat of the next section
  riser: (k, at, d, _f, v) => {
    noise(k, { at, dur: d, vol: 0.35 * v, filter: 'bandpass', f: 300, to: 7000, q: 1.2, attack: d * 0.85 });
    tone(k, { at, dur: d, vol: 0.08 * v, f: 60, to: 480, type: 'sawtooth', lp: 300, lpTo: 4000, attack: d * 0.8, detune: 10 });
  },
  // ---- pitched ----
  bass: (k, at, d, f, v) => {
    tone(k, { at, dur: d, vol: 0.32 * v, f, type: 'sawtooth', lp: f * (3 + 5 * v), lpTo: f * 1.6, q: 2.5, attack: 0.004, hold: d * 0.5 });
    tone(k, { at, dur: d, vol: 0.18 * v, f, type: 'sine', attack: 0.004, hold: d * 0.5 });
  },
  pad: (k, at, d, f, v) => supersaw(k, { at, dur: d + 0.3, vol: 0.09 * v, f, voices: 5, spread: 9, lp: 2500, attack: d > 1 ? 0.35 : 0.02, hold: d * 0.55, width: 0.8 }),
  // electric piano: a sine pair in FM for the body, a short high-ratio pair for the tine's attack
  keys: (k, at, d, f, v) => {
    fm(k, { at, dur: d, vol: 0.2 * v, f, ratio: 1, index: 1.3 + 0.8 * v, attack: 0.003, hold: d * 0.35, pan: keyPan(f) });
    fm(k, { at, dur: 0.08, vol: 0.05 * v, f, ratio: 14, index: 0.25, attack: 0.001, pan: keyPan(f) });
  },
  pluck: (k, at, d, f, v) => pluck(k, { at, dur: Math.max(0.4, d * 1.4), vol: 0.3 * v, f, bright: 0.75, pan: keyPan(f) }),
  lead: (k, at, d, f, v) => {
    tone(k, { at, dur: d + 0.15, vol: 0.17 * v, f, type: 'sawtooth', detune: 7, lp: 1800 + 1200 * v, lpTo: 900, q: 1, attack: 0.012, hold: d * 0.7 });
    tone(k, { at, dur: d + 0.1, vol: 0.06 * v, f: f / 2, type: 'sine', attack: 0.012, hold: d * 0.7 });
  },
};
